import { Service } from '@genial/services';
import { selector, selectorFamily } from 'recoil';

import { advisorIdToApisSelector } from '../master/selector';

import {
  normalizeTransactionFlowCurrentAccountResponse,
  normalizeTransactionFlowFixedIncomeResponse,
  normalizeTransactionFlowFundsResponse,
  normalizeTransactionFlowHistoricalSearchResponse,
  normalizeTransactionFlowPublicOfferResponse,
  normalizeTransactionFlowRequiredMarginResponse,
  normalizeTransactionFlowTreasuryResponse,
  normalizeTransactionFlowVariableIncomeResponse,
} from './normalizer';
import { TransactionFlowHistoricalSearch } from './types';

export const historicalSearchQuery = selectorFamily<
  | {
      data: TransactionFlowHistoricalSearch;
    }
  | undefined,
  { initialDate: Date; endDate: Date; productType?: number }
>({
  key: 'HistoricalSearch/HistoricalSearch',
  get: ({ initialDate, endDate, productType }) => {
    return async ({ get }) => {
      const advisorSelected = get(advisorIdToApisSelector);

      const filtersRequest = {
        screenName: 'transaction-flow-historical-search',
        filtroMovimentacoes: {
          dataMovimentoInicio: initialDate,
          dataMovimentoFim: endDate,
          idAssessor: advisorSelected,
          tipoProduto: productType,
        },
      };

      const response =
        await Service.External.Extranet.Movimentacao.V1.movimentacaoObterHistoricoMovimentacoesClientes(
          filtersRequest
        );

      const normalizedData = normalizeTransactionFlowHistoricalSearchResponse(response);

      return { data: normalizedData };
    };
  },
});

export const currentPositionAggregatedQuery = selector({
  key: 'currentPositionAggregatedQuery',
  get: async ({ get }) => {
    const advisorSelected = get(advisorIdToApisSelector);
    const filtersRequest = {
      screenName: 'transaction-flow-current-position',
      filtroMovimentacoes: {
        idAssessor: advisorSelected,
      },
    };

    const [
      responseFunds,
      responseCurrentAccount,
      responseRequiredMargin,
      responsePublicOffer,
      responseFixedIncome,
      responseVariableIncome,
      responseTreasury,
    ] = await Promise.allSettled([
      Service.External.Extranet.Movimentacao.V1.movimentacaoObterMovimentacoesFundosInvestimento(
        filtersRequest
      ),
      Service.External.Extranet.Movimentacao.V1.movimentacaoObterMovimentacoesContaCorrente(
        filtersRequest
      ),
      Service.External.Extranet.Movimentacao.V1.movimentacaoObterMovimentacoesMargemRequerida(
        filtersRequest
      ),
      Service.External.Extranet.Movimentacao.V1.movimentacaoObterMovimentacoesOfertaPublica(
        filtersRequest
      ),
      Service.External.Extranet.Movimentacao.V1.movimentacaoObterMovimentacoesRendaFixa(
        filtersRequest
      ),
      Service.External.Extranet.Movimentacao.V1.movimentacaoObterMovimentacoesRendaVariavel(
        filtersRequest
      ),
      Service.External.Extranet.Movimentacao.V1.movimentacaoObterMovimentacoesTesouroDireto(
        filtersRequest
      ),
    ]);

    const normalizedData = {
      funds: normalizeTransactionFlowFundsResponse(
        responseFunds.status === 'fulfilled' ? responseFunds.value : {}
      ),
      currentAccount: normalizeTransactionFlowCurrentAccountResponse(
        responseCurrentAccount.status === 'fulfilled' ? responseCurrentAccount.value : {}
      ),
      requiredMargin: normalizeTransactionFlowRequiredMarginResponse(
        responseRequiredMargin.status === 'fulfilled' ? responseRequiredMargin.value : {}
      ),
      publicOffer: normalizeTransactionFlowPublicOfferResponse(
        responsePublicOffer.status === 'fulfilled' ? responsePublicOffer.value : {}
      ),
      fixedIncome: normalizeTransactionFlowFixedIncomeResponse(
        responseFixedIncome.status === 'fulfilled' ? responseFixedIncome.value : {}
      ),
      variableIncome: normalizeTransactionFlowVariableIncomeResponse(
        responseVariableIncome.status === 'fulfilled' ? responseVariableIncome.value : {}
      ),
      treasury: normalizeTransactionFlowTreasuryResponse(
        responseTreasury.status === 'fulfilled' ? responseTreasury.value : {}
      ),
    };

    return normalizedData;
  },
});
